import { type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { type Bet } from '~/apps/product-plus/components/BettingInsights/types';
import { useClickoutBet } from '~/apps/product-plus/hooks/useClickoutBet.tsx';
import { useNonOCRepubBetslip } from '~/apps/product-plus/hooks/useNonOCRepubBetslip.tsx';
import { useOCRepubBetslip } from '~/apps/product-plus/hooks/useOCRepubBetslip.tsx';
import { useBetslipContext } from '~/providers/betslip-context';
import { useRepubContext } from '~/providers/repub-context.tsx';
import { getOddsByFormat } from '~/utils/getOddsByFormat.ts';

import { OddsButton, type OddsButtonProps } from '../cxy/cxy.ts';

import styles from './BetButton.module.css';

export type BetButtonProps = Omit<OddsButtonProps, 'odds'> & {
  bet: Bet;
  buttonClassName?: string;
  buttonStyles?: CSSProperties;
  label?: string;
  locked?: boolean;
  showLabel?: boolean;
  unformattedOdds: {
    oddsAmerican: string;
    oddsDecimal: number;
    oddsFractional: string;
  };
};

export const BetButton = ({
  bet,
  buttonClassName,
  buttonStyles,
  className,
  hideBookieLogo,
  label,
  locked,
  onClick,
  showLabel = true,
  unformattedOdds,
  ...props
}: BetButtonProps) => {
  const { bookieName } = props;
  const { repubIs } = useRepubContext();
  const { userOddsFormat } = useBetslipContext();
  const { t } = useTranslation();

  const clickoutBet = useClickoutBet({ bet, onClick });
  const ocRepubBetslip = useOCRepubBetslip({ bet, onClick });
  const nonOCRepubBetslip = useNonOCRepubBetslip({ bet, onClick, bookieName });

  // Determine which bet handler to use based on repub (more scalable solution)
  const getBetHandler = () => {
    if (repubIs.WS) {
      return clickoutBet;
    }

    if (repubIs.OC) {
      return ocRepubBetslip;
    }

    return nonOCRepubBetslip;
  };

  const { handleClick, isBetInBetslip } = getBetHandler();

  const odds = getOddsByFormat(unformattedOdds, userOddsFormat);

  return (
    <OddsButton
      aria-pressed={isBetInBetslip}
      data-state={isBetInBetslip ? 'on' : 'off'}
      hideBookieLogo={hideBookieLogo}
      locked={locked}
      odds={odds}
      onClick={locked ? undefined : handleClick}
      selected={isBetInBetslip}
      wrapperClassName={className}
      wrapperStyles={buttonStyles}
      {...props}
      className={clsx(styles.betButton, buttonClassName, {
        [styles.isSelected]: isBetInBetslip
      })}
      label={showLabel ? label || t('BEST_ODDS') : undefined} />);


};